if (!customElements.get("sample-box-builder")) {
  customElements.define(
    "sample-box-builder",
    class SampleBoxBuilder extends HTMLElement {
      constructor() {
        super();

        this.requiredQuantity = Number(this.dataset.requiredQuantity || 5);
        this.lines = Array.from(this.querySelectorAll("[data-product-line]"));
        this.totalTargets = Array.from(this.querySelectorAll("[data-selected-total]"));
        this.messageTarget = this.querySelector("[data-selection-message]");
        this.errorWrapper = this.querySelector("[data-error-message-wrapper]");
        this.errorMessage = this.querySelector("[data-error-message]");
        this.submitButton = this.querySelector("[data-submit]");
        this.spinner = this.querySelector(".loading-overlay__spinner");
        this.previewTitleTarget = this.querySelector("[data-preview-title-target]");
        this.previewCaptionTarget = this.querySelector("[data-preview-caption-target]");
        this.previewImageTarget = this.querySelector("[data-preview-target]");
        this.previewPlaceholder = this.querySelector(
          "[data-preview-target-placeholder]",
        );
        this.previewTriggers = Array.from(
          this.querySelectorAll("[data-preview-trigger]"),
        );
        this.cart = document.querySelector("cart-drawer");
        this.isSubmitting = false;
      }

      connectedCallback() {
        if (!this.lines.length) return;

        this.lines.forEach((line) => {
          const decreaseButton = line.querySelector("[data-quantity-decrease]");
          const increaseButton = line.querySelector("[data-quantity-increase]");
          const previewButton = line.querySelector(".sample-box-builder__product-main");

          decreaseButton?.addEventListener("click", () =>
            this.updateLineQuantity(line, -1),
          );
          increaseButton?.addEventListener("click", () =>
            this.updateLineQuantity(line, 1),
          );
          previewButton?.addEventListener("click", () =>
            this.setActivePreview(previewButton),
          );
        });

        this.previewTriggers.forEach((trigger) => {
          trigger.addEventListener("click", () => this.setActivePreview(trigger));
        });

        this.submitButton?.addEventListener("click", () => this.handleSubmit());
        this.initialize();
      }

      async initialize() {
        await this.hydrateHandleProducts();

        const initialPreview =
          this.querySelector(".sample-box-builder__thumb.is-active") ||
          this.lines[0];
        if (initialPreview) this.setActivePreview(initialPreview);

        this.render();
      }

      getLineQuantity(line) {
        return Number(line.querySelector("[data-quantity-input]")?.value || 0);
      }

      setLineQuantity(line, quantity) {
        const input = line.querySelector("[data-quantity-input]");
        if (input) input.value = quantity;
        line.classList.toggle("is-selected", quantity > 0);
      }

      getTotalQuantity() {
        return this.lines.reduce(
          (total, line) => total + this.getLineQuantity(line),
          0,
        );
      }

      updateLineQuantity(line, delta) {
        if (
          this.isSubmitting ||
          !line.dataset.variantId ||
          line.dataset.productAvailable !== "true"
        )
          return;

        const currentQuantity = this.getLineQuantity(line);
        const totalQuantity = this.getTotalQuantity();
        const nextQuantity = currentQuantity + delta;

        if (nextQuantity < 0) return;
        if (delta > 0 && totalQuantity >= this.requiredQuantity) return;

        this.setLineQuantity(line, nextQuantity);
        this.setActivePreview(line);
        this.clearError();
        this.render();
      }

      setActivePreview(source) {
        const previewSource = this.getPreviewSource(source);
        const previewId = previewSource?.dataset.previewId;
        if (!previewId) return;

        this.previewTriggers.forEach((trigger) => {
          trigger.classList.toggle("is-active", trigger.dataset.previewId === previewId);
        });

        this.lines.forEach((line) => {
          line.classList.toggle(
            "is-preview-active",
            line.dataset.previewId === previewId,
          );
        });

        if (this.previewTitleTarget && previewSource.dataset.previewTitle) {
          this.previewTitleTarget.textContent = previewSource.dataset.previewTitle;
        }

        if (this.previewCaptionTarget) {
          const caption = previewSource.dataset.previewCaption || "";
          this.previewCaptionTarget.textContent = caption;
          this.previewCaptionTarget.hidden = caption.length === 0;
        }

        const previewImage = previewSource.dataset.previewImage;
        if (!previewImage) return;

        if (this.previewImageTarget) {
          this.previewImageTarget.src = previewImage;
          this.previewImageTarget.srcset = previewImage;
          this.previewImageTarget.sizes = "100vw";
          this.previewImageTarget.alt = previewSource.dataset.previewTitle || "";
        } else {
          const image = document.createElement("img");
          image.className = "sample-box-builder__stage-image";
          image.setAttribute("data-preview-target", "true");
          image.src = previewImage;
          image.srcset = previewImage;
          image.sizes = "100vw";
          image.alt = previewSource.dataset.previewTitle || "";
          const mediaTarget = this.querySelector(".sample-box-builder__stage-media");
          mediaTarget?.appendChild(image);
          this.previewImageTarget = image;
        }

        if (this.previewPlaceholder) {
          this.previewPlaceholder.remove();
          this.previewPlaceholder = null;
        }
      }

      getPreviewSource(source) {
        if (!source) return null;
        if (source.dataset?.previewImage || source.dataset?.previewTitle) return source;

        if (source.matches?.("[data-product-line]")) {
          return (
            source.querySelector(".sample-box-builder__product-main") ||
            source.querySelector("[data-preview-trigger]") ||
            source
          );
        }

        return source.closest?.("[data-product-line]")?.querySelector(
          ".sample-box-builder__product-main",
        );
      }

      render() {
        const totalQuantity = this.getTotalQuantity();
        const remainingQuantity = this.requiredQuantity - totalQuantity;
        const ready = totalQuantity === this.requiredQuantity;

        this.totalTargets.forEach((target) => {
          target.textContent = totalQuantity;
        });

        if (this.messageTarget) {
          if (ready) {
            this.messageTarget.textContent = "Your sample box is ready to add to cart.";
          } else if (totalQuantity === 0) {
            this.messageTarget.textContent = `Select ${this.requiredQuantity} total samples to unlock checkout.`;
          } else {
            this.messageTarget.textContent = `Choose ${remainingQuantity} more sample${remainingQuantity === 1 ? "" : "s"} to continue.`;
          }
        }

        this.lines.forEach((line) => {
          const quantity = this.getLineQuantity(line);
          const decreaseButton = line.querySelector("[data-quantity-decrease]");
          const increaseButton = line.querySelector("[data-quantity-increase]");
          const isAvailable = line.dataset.productAvailable === "true";
          const hasVariantId = Boolean(line.dataset.variantId);

          if (decreaseButton)
            decreaseButton.disabled = !isAvailable || !hasVariantId || quantity === 0;
          if (increaseButton) {
            increaseButton.disabled =
              !isAvailable || !hasVariantId || totalQuantity >= this.requiredQuantity;
          }
        });

        if (this.submitButton) {
          const shouldDisable = !ready || this.isSubmitting;
          this.submitButton.disabled = shouldDisable;
          this.submitButton.setAttribute("aria-disabled", String(shouldDisable));
        }
      }

      buildPayload() {
        return this.lines
          .map((line) => ({
            id: Number(line.dataset.variantId),
            quantity: this.getLineQuantity(line),
          }))
          .filter((item) => item.id && item.quantity > 0);
      }

      async hydrateHandleProducts() {
        const handleLines = this.lines.filter(
          (line) => line.dataset.productHandle && !line.dataset.variantId,
        );

        if (!handleLines.length) return;

        await Promise.all(handleLines.map((line) => this.hydrateLine(line)));
      }

      async hydrateLine(line) {
        const handle = line.dataset.productHandle;
        if (!handle) return;

        try {
          const response = await fetch(
            `${window.location.origin}/products/${handle}.js`,
          );
          if (!response.ok) throw new Error("Unable to load product details.");

          const product = await response.json();
          const variant =
            product.variants?.find((item) => item.available) || product.variants?.[0];
          const isAvailable = Boolean(variant?.available);
          const previewId = line.dataset.previewId;
          const imageUrl = product.featured_image || product.images?.[0] || "";

          line.dataset.variantId = variant?.id || "";
          line.dataset.productAvailable = String(isAvailable);
          line.classList.toggle("is-disabled", !isAvailable);
          line.classList.remove("is-loading");

          if (imageUrl) {
            this.applyImageToElement(
              line.querySelector(".sample-box-builder__product-media"),
              imageUrl,
              line.dataset.productTitle || product.title,
              "sample-box-builder__product-image",
            );

            this.querySelectorAll(`[data-preview-id="${previewId}"]`).forEach((node) => {
              node.dataset.previewImage = imageUrl;
            });

            this.applyImageToElement(
              this.querySelector(
                `.sample-box-builder__thumb[data-preview-id="${previewId}"]`,
              ),
              imageUrl,
              line.dataset.productTitle || product.title,
              "sample-box-builder__thumb-image",
            );
          }

          const caption =
            line.querySelector("[data-preview-caption]")?.dataset.previewCaption ||
            line
              .querySelector(".sample-box-builder__product-main")
              ?.dataset.previewCaption ||
            "";
          const derivedCaption =
            caption ||
            (variant?.title && variant.title !== "Default Title" ? variant.title : "");

          const captionTarget = line.querySelector(".sample-box-builder__product-caption");
          if (captionTarget) {
            captionTarget.textContent = derivedCaption || "Ready to drink sample";
          }

          const trigger = line.querySelector(".sample-box-builder__product-main");
          if (trigger) {
            trigger.dataset.previewCaption = derivedCaption;
            trigger.dataset.previewTitle = line.dataset.productTitle || product.title;
          }

          const thumb = this.querySelector(
            `.sample-box-builder__thumb[data-preview-id="${previewId}"]`,
          );
          if (thumb) {
            thumb.dataset.previewCaption = derivedCaption;
            thumb.dataset.previewTitle = line.dataset.productTitle || product.title;
          }
        } catch (error) {
          line.classList.add("is-disabled");
          line.dataset.productAvailable = "false";
          const captionTarget = line.querySelector(".sample-box-builder__product-caption");
          if (captionTarget) {
            captionTarget.textContent = "Product unavailable";
          }
        }
      }

      applyImageToElement(container, imageUrl, altText, imageClass) {
        if (!container || !imageUrl) return;

        let image = container.querySelector("img");
        if (!image) {
          image = document.createElement("img");
          image.className = imageClass;
          container.innerHTML = "";
          container.appendChild(image);
        }

        image.src = imageUrl;
        image.srcset = imageUrl;
        image.sizes = "100vw";
        image.alt = altText || "";
      }

      showError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorWrapper.hidden = false;
      }

      clearError() {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = "";
        this.errorWrapper.hidden = true;
      }

      setLoadingState(isLoading) {
        this.isSubmitting = isLoading;
        this.submitButton?.classList.toggle("loading", isLoading);
        this.spinner?.classList.toggle("hidden", !isLoading);
        this.render();
      }

      async handleSubmit() {
        const totalQuantity = this.getTotalQuantity();
        if (totalQuantity !== this.requiredQuantity) {
          this.showError(
            `Please choose exactly ${this.requiredQuantity} total samples before adding to cart.`,
          );
          return;
        }

        const items = this.buildPayload();
        if (!items.length) {
          this.showError("Please choose at least one sample.");
          return;
        }

        this.clearError();
        this.setLoadingState(true);

        try {
          const config =
            typeof fetchConfig === "function"
              ? fetchConfig("json")
              : {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                };

          config.headers["X-Requested-With"] = "XMLHttpRequest";

          const sections =
            this.cart?.getSectionsToRender?.().map((section) => section.id) || [];

          config.body = JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          });

          if (this.cart?.setActiveElement) {
            this.cart.setActiveElement(document.activeElement);
          }

          const response = await fetch(`${routes.cart_add_url}`, config);
          const responseData = await response.json();

          if (!response.ok || responseData.status) {
            throw new Error(
              responseData.description ||
                responseData.message ||
                "There was a problem adding your sample box to the cart.",
            );
          }

          if (
            typeof publish === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined" &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "sample-box-builder",
              productVariantId: items[0]?.id,
              cartData: responseData,
            });
          }

          if (this.cart?.renderContents && responseData.sections) {
            const isClosedCart = !document.body.classList.contains(
              "page-overlay-cart-on",
            );
            this.cart.renderContents(responseData, isClosedCart);
          } else {
            window.location = window.routes.cart_url;
            return;
          }

          this.lines.forEach((line) => this.setLineQuantity(line, 0));
          this.render();
        } catch (error) {
          this.showError(
            error?.message ||
              "There was a problem adding your sample box to the cart.",
          );
        } finally {
          this.setLoadingState(false);
        }
      }
    },
  );
}
